import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Archive, ArrowCounterClockwise, Buildings, Calendar, CurrencyEur, Eye } from '@phosphor-icons/react';
import { fmtDateFR } from '../../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return fmtDateFR(iso); } catch { return '-'; }
};

export const ArchivedProjectsTab = ({ onOpen }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchArchived = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/projects`, { params: { only_archived: true }, withCredentials: true });
      setProjects(res.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchArchived(); }, [fetchArchived]);

  const handleUnarchive = async (id, name) => {
    if (!window.confirm(`Restaurer le chantier "${name}" ?`)) return;
    try {
      await axios.put(`${API_URL}/api/projects/${id}/unarchive`, {}, { withCredentials: true });
      toast.success('Chantier restaure');
      fetchArchived();
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API_URL}/api/projects/${confirmDelete.id}`, { withCredentials: true });
      toast.success('Chantier supprime definitivement');
      setConfirmDelete(null);
      fetchArchived();
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div data-testid="archived-projects-tab">
      <div style={{ marginBottom: '16px' }}>
        <p style={{ color: '#5E5F66', fontSize: '0.9rem', margin: 0 }}>
          {projects.length} chantier(s) archive(s). Vous pouvez les restaurer ou les supprimer definitivement.
        </p>
      </div>

      {projects.length === 0 ? (
        <div className="card" style={{ padding: '48px', textAlign: 'center', color: '#5E5F66' }}>
          <Archive size={48} style={{ opacity: 0.4, marginBottom: '12px' }} />
          <p style={{ margin: 0, fontWeight: '500' }}>Aucun chantier archive</p>
          <p style={{ fontSize: '0.85rem', marginTop: '4px' }}>Les chantiers archives apparaissent ici.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
          {projects.map(p => (
            <div key={p.id} className="card" data-testid={`archived-card-${p.id}`}
                 onClick={() => onOpen && onOpen(p)}
                 style={{ overflow: 'hidden', opacity: 0.95, cursor: onOpen ? 'pointer' : 'default', transition: 'transform 0.15s, box-shadow 0.15s' }}
                 onMouseEnter={(e) => { if (onOpen) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.08)'; } }}
                 onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}>
              <div style={{ padding: '16px 18px', borderBottom: '1px solid #F4F5F7', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '42px', height: '42px', borderRadius: '8px', backgroundColor: '#ECEFF1', color: '#546E7A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Buildings size={22} weight="bold" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '700', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#5E5F66', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.client_name || '-'}</div>
                </div>
                <span style={{ padding: '2px 8px', borderRadius: '4px', backgroundColor: '#ECEFF1', color: '#546E7A', fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase' }}>Archive</span>
              </div>
              <div style={{ padding: '14px 18px', fontSize: '0.85rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', color: '#5E5F66' }}>
                  <Calendar size={14} /> Archive le {fmtDate(p.archived_at)}
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', color: '#5E5F66' }}>
                  <CurrencyEur size={14} /> {(p.sale_price_ht || 0).toLocaleString('fr-FR')} HT
                </div>
                <div style={{ gridColumn: 'span 2', color: '#5E5F66' }}>
                  Facture : <strong style={{ color: '#1A1A1E' }}>{(p.total_invoiced_ht || 0).toLocaleString('fr-FR')} HT</strong>
                </div>
              </div>
              <div style={{ padding: '12px 18px', borderTop: '1px solid #F4F5F7', display: 'flex', gap: '8px', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                {onOpen && (
                  <button className="btn btn-secondary btn-sm" onClick={() => onOpen(p)} data-testid={`view-archived-${p.id}`} style={{ flex: 1, minWidth: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <Eye size={14} /> Consulter
                  </button>
                )}
                <button className="btn btn-primary btn-sm" onClick={() => handleUnarchive(p.id, p.name)} data-testid={`unarchive-${p.id}`} style={{ flex: 1, minWidth: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <ArrowCounterClockwise size={14} /> Restaurer
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => setConfirmDelete({ id: p.id, name: p.name })} data-testid={`delete-archived-${p.id}`}>
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '10px', width: '100%', maxWidth: '440px', padding: '24px' }} data-testid="delete-confirm-modal">
            <h3 style={{ margin: '0 0 10px 0', color: '#C62828' }}>⚠ Supprimer définitivement ?</h3>
            <p style={{ margin: '0 0 8px 0', fontSize: '0.92rem', color: '#1A1A1E' }}>
              Vous êtes sur le point de supprimer <strong>"{confirmDelete.name}"</strong>.
            </p>
            <p style={{ margin: '0 0 18px 0', fontSize: '0.85rem', color: '#5E5F66' }}>
              Cette action est <strong>irréversible</strong>. Toutes les données associées (dépenses, photos, documents, heures, réserves) seront définitivement effacées.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setConfirmDelete(null)} data-testid="cancel-delete-btn">Annuler</button>
              <button className="btn btn-danger" onClick={handleDelete} data-testid="confirm-delete-btn">Confirmer la suppression</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
